import { Avatar, Css } from "@homebound/beam";
import { useEffect } from "react";
import {
  CommentFragment,
  CommentStreamDetailFragment,
  useMarkCommentStreamAsReadMutation,
} from "src/components/comments/CommentFeed.generated";
import { CommentEditor, CommentRow } from "src/components/index";
import { CommentStreamVisibility, InternalUser } from "src/generated/graphqlTypes";
import { useIntersectionObserver } from "usehooks-ts";
import { SaveAttachmentModel } from "../boundAttachments/BoundAttachments";
import { CommentDeleteFunction, CommentSaveFunction } from "./CommentFeed";

export type CommentStreamProps = {
  stream?: CommentStreamDetailFragment;
  streamVisibility: CommentStreamVisibility;
  saveComment: CommentSaveFunction;
  deleteComment: CommentDeleteFunction;
  currentUser: InternalUser;
  commentableId: string;
  maxHeight?: number;
  // Defaults to true;
  showAvatar?: boolean;
};

export function CommentStream(props: CommentStreamProps) {
  const {
    stream,
    streamVisibility,
    saveComment,
    deleteComment,
    currentUser,
    commentableId,
    maxHeight,
    showAvatar = true,
  } = props;
  const commentStreamId = stream?.id;
  const comments = stream?.comments || [];
  const { name, avatar } = currentUser;
  const { ref, isIntersecting } = useIntersectionObserver({ freezeOnceVisible: true });
  const [markCommentStreamAsRead, { called: mutationFired }] = useMarkCommentStreamAsReadMutation();

  useEffect(() => {
    if (stream && stream?.unreadCount > 0 && isIntersecting && !mutationFired) {
      void markCommentStreamAsRead({ variables: { input: { id: stream.id } } });
    }
  }, [stream, isIntersecting, mutationFired, markCommentStreamAsRead]);

  async function handleSave(text: string, html: string, attachments: SaveAttachmentModel[], mentions?: string[]) {
    await saveComment(text, html, attachments, { commentStreamId, streamVisibility }, mentions);
  }

  return (
    /**
     * fdcr: Render bottom-up with Y-overflow scrolling off the top of the component, ensuring
     *    users see the Editor and most recent comments on page-load
     * maxh: "lgtm" values picked after comparing 1080p and 1440p monitors on the ProjectOverview
     *    page. Some constraint is required for overflow+fdcr to work as desired.
     *    For parent containers with a fixed height, we can then just pass a custom maxHeight here
     */
    <div ref={ref} css={Css.maxh(maxHeight ? `${maxHeight}px` : "calc(max(65vh, 900px))").oya.oxh.df.fdcr.$}>
      <div css={Css.df.$}>
        {showAvatar && (
          <div css={Css.mr2.fs0.$}>
            <Avatar name={name} src={avatar} />
          </div>
        )}
        <CommentEditor
          commentableId={commentableId}
          commentStreamId={commentStreamId}
          streamVisibility={streamVisibility}
          onSave={handleSave}
          submitText="Add"
        />
      </div>
      <div css={Css.mt2.$}>
        {comments.map((comment: CommentFragment) => {
          return (
            <div css={Css.mb4.$} key={comment.id}>
              <CommentRow {...{ comment, currentUser, saveComment, deleteComment }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
